import {
  Box,
  Button,
  Card,
  Typography,
  Divider,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import FilePicker from "components/FilePicker";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import Table from "components/Table";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useState, useMemo } from "react";
import sessionHelper from "services/sessionHelper";
import { getTranslation } from "services/translationService";
import { uploadTranslation } from "services/uploadService";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import TranslateIcon from "@mui/icons-material/Translate";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ClearIcon from "@mui/icons-material/Clear";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  card: {
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "#ffffff",
    transition: "box-shadow 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    "& svg": {
      fontSize: "1.5rem",
      color: theme.palette.primary.main,
    },
  },
  filterSection: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 12,
    alignItems: "flex-end",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  field: {
    minWidth: 220,
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  uploadSection: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 16,
    alignItems: "flex-end",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
    paddingBottom: 8,
    flexWrap: "wrap",
    gap: 16,
  },
  searchBox: {
    flex: 1,
    maxWidth: 500,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  statsChip: {
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  tableContainer: {
    "& .MuiTableCell-root": {
      fontSize: "0.95rem",
      padding: "14px 16px",
    },
    "& .MuiTableCell-head": {
      fontSize: "1rem",
      fontWeight: 600,
      padding: "16px",
      whiteSpace: "nowrap",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#999",
  },
  infoBox: {
    padding: "12px 16px",
    borderRadius: "8px",
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    "& svg": {
      color: "#1976d2",
    },
  },
}));

const Translation = () => {
  const styles = useStyles();
  const [loading, setLoading] = React.useState(false);
  const { showSnackbar } = useSnackbar();
  const [list, setList] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [fileDetails, setFileDetails] = React.useState({
    file: null,
    fileType: ".xlsx, .xls",
  });

  const [filterOptions, setFilterOptions] = useState({
    isoCode: null,
  });

  // Filter list based on search
  const filteredList = useMemo(() => {
    if (!searchKey.trim()) return list;

    const searchLower = searchKey.toLowerCase();
    return list.filter(
      (item: any) =>
        item.key?.toLowerCase().includes(searchLower) ||
        item.value?.toLowerCase().includes(searchLower)
    );
  }, [list, searchKey]);

  const onSelectFile = (file: any) => {
    if (!file) {
      setFileDetails((st) => ({ ...st, file: null }));
      return;
    }
    setFileDetails((st) => ({ ...st, file }));
  };

  const onUpload = async () => {
    try {
      setLoading(true);
      const response = await uploadTranslation(fileDetails.file);
      setLoading(false);
      setFileDetails((state) => ({ ...state, file: null }));
      showSnackbar(response.message);
      if (!response.status) throw new Error(response?.message);
      // Reset search and language selection after upload
      setSearchKey("");
      setFilterOptions({ isoCode: null });
      setList([]);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const getTranslationData = async () => {
    try {
      setLoading(true);
      const response = await getTranslation(filterOptions);
      setList(response?.data);
      setSearchKey(""); // Reset search when loading new data
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e.target;
    setFilterOptions((st) => ({ ...st, [name]: value }));
  };

  return (
    <ContentShell title="Translation Management" loading={loading}>
      <Box className={styles.mainContainer}>
        <Grid container spacing={3}>
          {/* Filter Section */}
          <Grid item xs={12} md={6}>
            <Card className={styles.card}>
              <Box className={styles.sectionHeader}>
                <TranslateIcon />
                <Typography variant="h6" fontWeight={600}>
                  Load Translations
                </Typography>
              </Box>
              <Box className={styles.infoBox}>
                <InfoOutlinedIcon fontSize="small" />
                <Typography variant="body2">
                  Select a language to view and manage translations
                </Typography>
              </Box>
              <Box className={styles.filterSection}>
                <Box className={styles.field}>
                  <SimpleSelectInput
                    name="isoCode"
                    label="Select Language"
                    onChange={onChange}
                    options={sessionHelper.languageOptions}
                    value={filterOptions.isoCode}
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  onClick={getTranslationData}
                  disabled={!filterOptions.isoCode}
                  startIcon={<SearchIcon />}
                  fullWidth={false}
                  size="large"
                >
                  Load Translations
                </Button>
              </Box>
            </Card>
          </Grid>

          {/* Upload Section */}
          <Grid item xs={12} md={6}>
            <Card className={styles.card}>
              <Box className={styles.sectionHeader}>
                <UploadFileIcon />
                <Typography variant="h6" fontWeight={600}>
                  Upload Translations
                </Typography>
              </Box>
              <Box className={styles.infoBox}>
                <InfoOutlinedIcon fontSize="small" />
                <Typography variant="body2">
                  Upload Excel file (.xlsx, .xls) to update translations
                </Typography>
              </Box>
              <Box className={styles.uploadSection}>
                <Box flex={1}>
                  <FilePicker
                    label="Select Translation File"
                    name="translation"
                    fileType={fileDetails.fileType}
                    value={fileDetails.file}
                    showPreview={false}
                    onFileSelect={onSelectFile}
                  />
                </Box>
                <Button
                  disabled={!fileDetails.file}
                  type="submit"
                  variant="contained"
                  onClick={onUpload}
                  startIcon={<UploadFileIcon />}
                  size="large"
                >
                  Upload File
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Translations List Section */}
        {list.length > 0 && (
          <Card className={styles.card}>
            <Box className={styles.tableHeader}>
              <Box display="flex" alignItems="center" gap={1}>
                <ListAltIcon color="primary" fontSize="large" />
                <Typography variant="h6" fontWeight={600}>
                  Translation Entries
                </Typography>
                <Chip
                  label={`${filteredList.length} of ${list.length}`}
                  color="primary"
                  size="small"
                  className={styles.statsChip}
                />
              </Box>
              <Box className={styles.searchBox}>
                <SimpleTextInput
                  label="Search Translations"
                  name="search"
                  value={searchKey}
                  onChange={(e: any) => setSearchKey(e.target.value)}
                  placeholder="Search by key or value..."
                  InputProps={{
                    endAdornment: searchKey && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          onClick={() => setSearchKey("")}
                          edge="end"
                          size="small"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {filteredList.length > 0 ? (
              <Box className={styles.tableContainer}>
                <Table
                  columns={[
                    {
                      id: "isoCode",
                      label: "ISO Code",
                      numeric: false,
                    },
                    {
                      id: "key",
                      label: "Translation Key",
                      numeric: false,
                    },
                    {
                      id: "value",
                      label: "Translation Value",
                      numeric: false,
                    },
                  ]}
                  data={filteredList}
                  highlightAltRow={true}
                  hidePagination={true}
                />
              </Box>
            ) : (
              <Box className={styles.emptyState}>
                <SearchIcon
                  style={{
                    fontSize: "3rem",
                    marginBottom: "16px",
                    opacity: 0.3,
                  }}
                />
                <Typography variant="h6" color="textSecondary">
                  No translations found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try adjusting your search criteria
                </Typography>
              </Box>
            )}
          </Card>
        )}
      </Box>
    </ContentShell>
  );
};

export default Translation;
